import { unMaskPrice } from '@/utils/common';

/**
 * Validates if a currency value is greater than zero
 * @param {string|number} value - The value to check
 * @returns {boolean} - True if value is greater than zero
 */
const checkCurrency = (value) => {
  if (!value) return false;

  try {
    const money = typeof value === 'string' ? unMaskPrice(value) : value;
    return money > 0;
  } catch (error) {
    console.error('Error validating currency:', error);
    return false;
  }
};

/**
 * Checks if a string contains special characters
 * @param {string} string - The string to check
 * @returns {boolean} - True if no special characters found
 */
const hasSpecialCharacters = (string) => {
  if (!string) return true;

  try {
    const regexSpecialCharacters = /[\s~`!@#$%^&*+=[\]\\';,/{}|\\":<>?()._]/g;
    const normalizedString = string.normalize().trim();
    return !regexSpecialCharacters.test(normalizedString);
  } catch (error) {
    console.error('Error validating special characters:', error);
    return false;
  }
};

/**
 * Checks if a string contains Vietnamese accented characters
 * @param {string} string - The string to check
 * @returns {boolean} - True if no Vietnamese accented characters found
 */
const checkAscent = (string) => {
  if (!string) return true;

  try {
    const regexAscent =
      /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/gi;
    return !regexAscent.test(string);
  } catch (error) {
    console.error('Error validating accented characters:', error);
    return false;
  }
};

/**
 * Create validator functions for use with vuelidate
 * @returns {Object} - Object containing validator functions
 */
export const createValidators = () => {
  return {
    required: (val) => !!val,

    maxLength: (length) => (val) => !val || val.length <= length,

    minLength: (length) => (val) => !val || val.length >= length,

    hasSpecialCharacters: (val) => {
      if (!val) return true;
      const regexSpecialCharacters = /[\s~`!@#$%^&*+=[\]\\';,/{}|\\":<>?()._]/g;
      const normalizedString = val.normalize().trim();
      return !regexSpecialCharacters.test(normalizedString);
    },

    checkAscent: (val) => {
      if (!val) return true;
      const regexAscent =
        /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/gi;
      return !regexAscent.test(val);
    },

    checkCurrency: (val) => {
      if (!val) return false;
      const money = unMaskPrice(val);
      return money > 0;
    },
  };
};

/**
 * Validate product form data
 * @param {Object} data - Form data to validate
 * @returns {Object} - Validation result containing isValid and errors
 */
export const validateProductForm = (data) => {
  const errors = {};

  // Check product name
  if (!data.name) {
    errors.name = 'Yêu cầu nhập Tên sản phẩm';
  }

  // Check product code
  if (!data.code) {
    errors.code = 'Yêu cầu nhập Mã sản phẩm';
  } else {
    if (data.code.length < 3) {
      errors.code = 'Vui lòng nhập mã lớn hơn 3 kí tự';
    } else if (data.code.length > 50) {
      errors.code = 'Vui lòng nhập mã nhỏ hơn 50 kí tự';
    } else if (!hasSpecialCharacters(data.code)) {
      errors.code = 'Mã sản phẩm không được chứa các ký tự đặc biệt';
    } else if (!checkAscent(data.code)) {
      errors.code = 'Mã sản phẩm không được chứa kí tự tiếng việt';
    }
  }

  // Check category
  if (!data.selectedCate) {
    errors.selectedCate = 'Yêu cầu chọn Danh mục sản phẩm';
  }

  // Check brand
  if (!data.searchBrandName) {
    errors.searchBrandName = 'Yêu cầu chọn Thương hiệu';
  }

  // Check listed price
  // if (!data.listedPrice) {
  //   errors.listedPrice = 'Yêu cầu nhập Giá niêm yết';
  // } else if (!checkCurrency(data.listedPrice)) {
  //   errors.listedPrice = 'Số tiền phải lớn hơn 0';
  // }

  // Check unit
  if (!data.unitId) {
    errors.unitId = 'Yêu cầu chọn đơn vị tính';
  }

  // Check VAT
  if (data.productVat === null || data.productVat === undefined) {
    errors.productVat = 'Yêu cầu nhập thuế suất';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const productValidators = {
  checkCurrency,
  hasSpecialCharacters,
  checkAscent,
  createValidators,
  validateProductForm,
};
